import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  alpha,
} from '@mui/material';
import {  Sprout, Target, Rocket, Crown, Trophy } from 'lucide-react';
import { default as farmer } from '../assets/farmer1.webp';
import member1 from '../assets/member1.png';
import member2 from '../assets/member2.png';
import member3 from '../assets/member3.png';
import member4 from '../assets/member4.png';
import member5 from '../assets/member5.png';
import member6 from '../assets/member6.png';
import theme from '../theme';
import pageContent from '../data/pageContent.json';
import SEO from '../components/SEO';

const About = () => {
  const { t } = useTranslation('about');
  const { committee: committeePageContent } = pageContent.about;

  // Get translated committee member names and titles, then merge with image paths from pageContent
  const committee = {
    ...committeePageContent,
    members: (t('committee.members', { returnObjects: true }) || []).map((member, index) => ({
      name: member.name,
      organization: member.organization,
      image: committeePageContent.members[index]?.image
    }))
  };

  // Member image mapping
  const memberImages = {
    '/assets/member1.png': member1,
    '/assets/member2.png': member2,
    '/assets/member3.png': member3,
    '/assets/member4.png': member4,
    '/assets/member5.png': member5,
    '/assets/member6.png': member6,
  };

  // Icon mapping
  const iconMap = {
    Sprout,
    Target,
    Rocket,
    Crown,
    Trophy
  };

  // Get translated sponsorship categories
  const sponsorshipIcons = ['Crown', 'Trophy', 'Rocket', 'Crown'];
  const sponsorshipCategories = t('sponsorship.categories', { returnObjects: true }).map((cat, index) => ({
    ...cat,
    icon: React.createElement(iconMap[sponsorshipIcons[index]])
  }));

  // Get translated mission cards
  const missionIcons = ['Sprout', 'Target', 'Rocket'];
  const missionCards = t('mission.cards', { returnObjects: true }).map((card, index) => ({
    ...card,
    icon: React.createElement(iconMap[missionIcons[index]]),
    color: '#B8901C'
  }));

  return (
    <Box sx={{ backgroundColor: '#FFFFFF' }}>
      {/* SEO Component */}
      <SEO
        titleKey="meta.title"
        descriptionKey="meta.description"
      />

      {/* Hero */}
      <Box sx={{
        position: 'relative',
        pt: { xs: "160px", lg: "120px" },
        pb: { xs: "60px", lg: "80px" },
        minHeight: { xs: '50vh', md: '70vh' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        overflow: 'visible',
        backgroundImage: `url("https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=1920&h=1080&fit=crop")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}>
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle, rgba(212, 165, 32, 0.5) 0%, rgba(0, 0, 0, 0.9) 100%, rgba(0, 0, 0, 0.9) 100%);',
          }}
        />

        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'secondary.light',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: { xs: '0.75rem', md: '0.875rem' },
              }}
            >
              {t('hero.overline')}
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 'bold',
                mb: 3,
                lineHeight: 1.1,
                background: 'linear-gradient(135deg, #1A1A1A 27%, #D4A747 50%, #1A1A1A 73%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {t('hero.title')}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                mb: 4,
                opacity: 0.9,
                maxWidth: 800,
                color: theme.palette.secondary.main,
                mx: 'auto'
              }}
            >
              {t('hero.subtitle')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '0.95rem', md: '1.1rem' },
                opacity: 0.8,
                maxWidth: 1000,
                px: { xs: 2, md: 0 },
                color: theme.palette.secondary.main,
                mx: 'auto'
              }}
            >
              {t('hero.description')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Overview Section */}
      <Box sx={{
        py: { xs: 8, md: 10 },
        background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(212, 167, 71, 0.08) 0%, transparent 70%)',
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -150,
            left: -150,
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(184, 144, 28, 0.05) 0%, transparent 70%)',
            zIndex: 0,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('overview.overline')}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 900,
                  mb: 2
                }}
              >
                {t('overview.sectionTitle')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 700, mx: 'auto' }}>
                {t('overview.subtitle')}
              </Typography>
            </Box>
          </motion.div>

          {/* Enhanced Book/Page Design */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Box
              sx={{
                position: 'relative',
                maxWidth: 1000,
                mx: 'auto',
              }}
            >
              {/* Book Page Container */}
              <Box
                sx={{
                  position: 'relative',
                  background: '#FFFFFF',
                  borderRadius: '0 20px 20px 0',
                  px: { xs: 3, md: 6 },
                  py: 4,
                  boxShadow: `
                    0 15px 50px rgba(0, 0, 0, 0.1),
                    inset 4px 0 8px rgba(212, 167, 71, 0.15)
                  `,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `
                      0 20px 60px rgba(0, 0, 0, 0.15),
                      inset 4px 0 8px rgba(212, 167, 71, 0.2)
                    `,
                  },
                  // Left spine binding - enhanced
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 20,
                    background: `
                      linear-gradient(90deg,
                        rgba(212, 167, 71, 0.35) 0%,
                        rgba(212, 167, 71, 0.25) 40%,
                        rgba(212, 167, 71, 0.1) 70%,
                        rgba(212, 167, 71, 0.05) 100%
                      )
                    `,
                    borderRadius: '0 0 0 0',
                  },
                  // Vertical center line
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 8,
                    top: '8%',
                    bottom: '8%',
                    width: 3,
                    background: 'linear-gradient(180deg, transparent 0%, rgba(212, 167, 71, 0.5) 15%, rgba(184, 144, 28, 0.6) 50%, rgba(212, 167, 71, 0.5) 85%, transparent 100%)',
                    borderRadius: '2px',
                  }
                }}
              >
                {/* Bookmark Ribbon - Enhanced */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: 60,
                    width: 40,
                    height: 90,
                    background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                    clipPath: 'polygon(0 0, 100% 0, 100% 88%, 50% 100%, 0 88%)',
                    boxShadow: '3px 5px 15px rgba(212, 167, 71, 0.5)',
                    zIndex: 2,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '40%',
                      background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%)',
                    }
                  }}
                />

                {/* Decorative Header Ornament */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 5,
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      height: 2,
                      background: 'linear-gradient(90deg, transparent 0%, #D4A747 100%)',
                      opacity: 0.4,
                    }}
                  />
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(212, 167, 71, 0.15) 0%, rgba(184, 144, 28, 0.15) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid',
                      borderColor: 'rgba(212, 167, 71, 0.3)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      height: 2,
                      background: 'linear-gradient(90deg, #D4A747 0%, transparent 100%)',
                      opacity: 0.4,
                    }}
                  />
                </Box>

                {/* Content */}
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  {t('overview.paragraphs', { returnObjects: true }).map((paragraph, index) => (
                    <Typography
                      key={index}
                      variant="body1"
                      sx={{
                        mb: index < t('overview.paragraphs', { returnObjects: true }).length - 1 ? 4 : 0,
                        color: '#2C3E50',
                        lineHeight: 2,
                        fontSize: '1.05rem',
                        textAlign: 'left',
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Box>

                {/* Decorative Footer Ornament */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 5,
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      height: 2,
                      background: 'linear-gradient(90deg, transparent 0%, #D4A747 100%)',
                      opacity: 0.4,
                    }}
                  />
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(212, 167, 71, 0.15) 0%, rgba(184, 144, 28, 0.15) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid',
                      borderColor: 'rgba(212, 167, 71, 0.3)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      height: 2,
                      background: 'linear-gradient(90deg, #D4A747 0%, transparent 100%)',
                      opacity: 0.4,
                    }}
                  />
                </Box>
              </Box>

              {/* Spine Stitching Details */}
              {[...Array(6)].map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    position: 'absolute',
                    left: 5,
                    top: `${15 + i * 12}%`,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(184, 144, 28, 0.4) 0%, rgba(212, 167, 71, 0.2) 70%, transparent 100%)',
                    border: '1px solid rgba(212, 167, 71, 0.3)',
                    zIndex: 1,
                  }}
                />
              ))}

            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Our Inspiration */}
      <Box sx={{ py: { xs: 8, md: 6 }, backgroundColor: 'white' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, lg: 6 }} sx={{ order: { xs: 2, lg: 1 } }}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      letterSpacing: 1.5,
                      fontSize: '0.875rem',
                    }}
                  >
                    {t('inspiration.overline')}
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      fontWeight: 900,
                    }}
                  >
                    {t('inspiration.sectionTitle')}
                  </Typography>
                </Box>

                {t('inspiration.content', { returnObjects: true }).map((paragraph, index) => (
                  <Typography
                    key={index}
                    variant="body1"
                    sx={{
                      mb: index < t('inspiration.content', { returnObjects: true }).length - 1 ? 3 : 4,
                      color: '#6C757D',
                      lineHeight: 1.8,
                    }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </motion.div>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }} sx={{ order: { xs: 1, lg: 2 } }}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    transform: 'rotate(-2deg)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'rotate(0deg) scale(1.02)',
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={farmer}
                    alt={t('inspiration.imageCaption')}
                    sx={{
                      width: '100%',
                      height: { xs: '250px', sm: '300px', md: '400px' },
                      objectFit: 'cover',
                      borderRadius: '20px',
                    }}
                  />

                  {/* Image Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      color: 'white',
                      p: { xs: 2, md: 3 },
                      borderRadius: '0 0 20px 20px',
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t('inspiration.imageCaption')}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {t('inspiration.imageSubtitle')}
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* About Bhoomee Awards */}
      <Box sx={{ py: { xs: 8, md: 6 }, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('mission.overline')}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 900
                }}
              >
                {t('mission.sectionTitle')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mt: 2, maxWidth: 600, mx: 'auto' }}>
                {t('mission.subtitle')}
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={4}>
            {missionCards.map((card, index) => (
              <Grid size={{ xs: 12, lg: 4 }} key={index}>
                <motion.div
                  initial={{ opacity: 0, x: index === 0 ? -30 : index === 1 ? 0 : 30, y: index === 1 ? 20 : 0 }}
                  whileInView={{ opacity: 1, x: 0, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true }}
                  style={{ height: '100%', display: 'flex' }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '16px',
                      background: '#ffffff',
                      border: 'none',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.4s ease',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent 0%, #D4A747 50%, transparent 100%)',
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(59, 127, 60, 0.1), 0 3px 10px rgba(0, 0, 0, 0.05)',
                        '& .minimal-accent': {
                          width: '100%',
                          opacity: 1,
                        },
                        '& .icon-minimal': {
                          transform: 'scale(1.05)',
                          background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                        },
                        '& .text-reveal': {
                          opacity: 1,
                          transform: 'translateY(0)',
                        }
                      }
                    }}
                  >
                    <Box
                      className="minimal-accent"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '30%',
                        height: '3px',
                        background: 'linear-gradient(90deg, #D4A747 0%, #B8901C 100%)',
                        transition: 'all 0.6s ease',
                        opacity: 0.7,
                      }}
                    />

                    <CardContent sx={{ p: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Box
                          className="icon-minimal"
                          sx={{
                            width: 64,
                            height: 64,
                            mx: 'auto',
                            mb: 3,
                            background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.4s ease',
                            boxShadow: '0 4px 12px rgba(59, 127, 60, 0.2)',
                          }}
                        >
                          {card.icon}
                        </Box>

                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            mb: 2,
                            color: card.color,
                            fontSize: '1.5rem',
                            letterSpacing: '-0.01em'
                          }}
                        >
                          {card.title}
                        </Typography>
                      </Box>

                      <Typography
                        className="text-reveal"
                        variant="body1"
                        sx={{
                          color: '#6b7280',
                          lineHeight: 1.7,
                          fontSize: '0.95rem',
                          textAlign: 'center',
                          fontWeight: 400,
                          opacity: 0.9,
                          mb: 2,
                          transform: 'translateY(10px)',
                          transition: 'all 0.4s ease 0.1s',
                          flex: 1
                        }}
                      >
                        {card.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Sponsor Bhoomee Awards */}
      <Box sx={{ py: { xs: 8, md: 6 }, backgroundColor: 'white' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('sponsorship.overline')}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 900
                }}
              >
                {t('sponsorship.sectionTitle')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mt: 2, maxWidth: 600, mx: 'auto' }}>
                {t('sponsorship.subtitle')}
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={4}>
            {sponsorshipCategories.map((category, index) => (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: '24px',
                    background: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: `0 12px 40px ${alpha('#D4A747', 0.2)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 40px ${alpha('#D4A747', 0.2)}`,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '80px',
                      background: `linear-gradient(135deg, ${alpha('#FFF8E1', 0.3)} 0%, ${alpha('#D4A747', 0.1)} 100%)`,
                      clipPath: 'polygon(0 0, 100% 0, 100% 60%, 0 100%)',
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 4 }, position: 'relative', zIndex: 1 }}>
                    <Box
                      sx={{
                        width: { xs: 48, md: 64 },
                        height: { xs: 48, md: 64 },
                        borderRadius: '16px',
                        background: `linear-gradient(135deg, #D4A747 0%, #B8901C 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        mb: 3,
                      }}
                    >
                      {React.cloneElement(category.icon, { sx: { fontSize: 32 } })}
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      gutterBottom
                      sx={{ color: 'text.primary', fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif', mb: 2 }}
                    >
                      {category.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.6} sx={{ fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' }}>
                      {category.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Committee Members */}
      <Box sx={{ py: { xs: 8, md: 6 }, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('committee.overline')}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 900
                }}
              >
                {t('committee.sectionTitle')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mt: 2, maxWidth: 600, mx: 'auto' }}>
                {t('committee.subtitle')}
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={4}>
            {committee.members.map((member, index) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={index} sx={{ display: 'flex' }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{ width: '100%', display: 'flex' }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '24px',
                      background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                      border: 'none',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      overflow: 'hidden',
                      position: 'relative',
                      cursor: 'pointer',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    }}
                  >
                    {/* Background Pattern */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(135deg, rgba(212, 167, 71, 0.03) 0%, rgba(184, 144, 28, 0.03) 100%)`,
                        opacity: 0.5,
                      }}
                    />

                    {/* Content Container */}
                    <Box sx={{ p: 4, textAlign: 'center', position: 'relative', zIndex: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>

                      {/* Profile Image */}
                      <Box
                        sx={{
                          position: 'relative',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          mb: 3,
                        }}
                      >
                        <Box
                          className="profile-image"
                          sx={{
                            width: 100,
                            height: 100,
                            borderRadius: '50%',
                            backgroundImage: `url(${memberImages[member.image] || member.image})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            border: '4px solid white',
                            boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              inset: -4,
                              borderRadius: '50%',
                              background: `linear-gradient(135deg, #D4A747 0%, #B8901C 100%)`,
                              zIndex: -1,
                            }
                          }}
                        />
                      </Box>

                      {/* Content */}
                      <Box
                        className="content-wrapper"
                        sx={{
                          transition: 'transform 0.4s ease',
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: '#1a1a1a',
                            mb: 1,
                            fontSize: '1.3rem',
                            lineHeight: 1.2,
                            letterSpacing: '-0.02em',
                          }}
                        >
                          {member.name}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#D4A747',
                            fontWeight: 500,
                            mb: 2.5,
                            fontSize: '0.85rem',
                            lineHeight: 1.4,
                            opacity: 0.9,
                          }}
                        >
                          {member.organization.split('\n').map((line, i) => (
                            <React.Fragment key={i}>
                              {line}
                              {i < member.organization.split('\n').length - 1 && <br />}
                            </React.Fragment>
                          ))}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#6b7280',
                            lineHeight: 1.6,
                            fontSize: '0.85rem',
                            maxWidth: '280px',
                            mx: 'auto',
                          }}
                        >
                          {member.description}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Bottom Decoration */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 60,
                        height: 4,
                        borderRadius: '2px 2px 0 0',
                        background: `linear-gradient(90deg, #D4A747 0%, #D4A747 100%)`,
                        opacity: 0.6,
                      }}
                    />
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default About;